import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import {
  actionsWidth,
  itemWidth
} from '~/modules/common/charts/timeline/calculations';
import {
  ShowMoreRow,
  ProjectChartRowsLoading,
  ShowResourceMergeDialogOrLoading
} from '~/modules/resourcing/common/components';
import { dateToMidnightUTCString } from '~/modules/common/dates/convert';
import useMeContext from '~/modules/me/useMeContext';
import { useResourceRequestToolbarContext } from '~/modules/projects/resourcing-plan/hooks';

import { useResourceActualSeriesForProjectResources } from '~/modules/resourcing/common/hooks';
import {
  buildScheduleRulesWithDefaultConstraint,
  getResourceRequestDatesFromProjectDate
} from '~/modules/resourcing/common/util';
import { useDialogState } from '~/modules/common/hooks';
import ShowSmartFitDialogOrLoading from '~/modules/resourcing/components/ResourceAssignmentDialog/components/ResourceUsersChartPanel/components/ShowSmartFitDialogOrLoading';
import GroupTitleHeader from '../GroupTitleHeader';
import { ResourceAllocationChartRow } from './components';

const useStyles = makeStyles(theme => ({
  containerReadonly: {
    padding: theme.spacing(0.5, 2, 0, 1.5)
  }
}));

const ResourceAllocationChart = ({
  project,
  resourceAllocations,
  scale,
  chartDates,
  isEditSwitchEnabled,
  handleAddResourceAllocation,
  smartFitResourceAllocation,
  openSmartFitDialogOrLoadingState,
  closeShowSmartFitDialogOrLoading,
  onSmartFitCancel,
  loadingRows,
  hasMoreRows,
  loadMoreRows,
  handleResourceChangeAndUpdateResourceAllocation,
  handleEditResourceAllocation,
  handleAllocatedUserChange,
  handleAllocatedRoleChange,
  loadingMore,
  createAllocationLoading,
  setSmartFitResourceAllocation,
  updateAllocationLoading,
  handleNewMergeResourceAllocation
}) => {
  const {
    open: resourceMergeDialogOpen,
    openDialog: openResourceMergeDialog,
    closeDialog: closeResourceMergeDialog
  } = useDialogState(false);

  const [
    resourceMergeDialogAllocation,
    setResourceMergeDialogAllocation
  ] = useState({});

  const classes = useStyles();
  const leftComponentWidth = actionsWidth + itemWidth * 2;
  const {
    featureFlags: { isPSARMPMergeResourcesEnabled }
  } = useMeContext();
  const {
    isResourceActualModeEnabled,
    filter,
    sort
  } = useResourceRequestToolbarContext();

  const {
    requestStartDate: allocationStartDate,
    requestEndDate: allocationEndDate
  } = getResourceRequestDatesFromProjectDate({
    projectStartDate: project.startDate,
    projectEndDate: project.endDate,
    scale
  });

  const defaultSmartFitScheduleRules = useMemo(
    () =>
      buildScheduleRulesWithDefaultConstraint({
        startDate: dateToMidnightUTCString(allocationStartDate),
        endDate: dateToMidnightUTCString(allocationEndDate),
        defaultScheduleRule: project.defaultScheduleRule
      }),
    [allocationEndDate, allocationStartDate, project.defaultScheduleRule]
  );

  const onMergeResourceClose = useCallback(() => {
    closeResourceMergeDialog();
    onSmartFitCancel();
  }, [closeResourceMergeDialog, onSmartFitCancel]);

  const onSubmit = useCallback(
    ({ overrideScheduleRules, isMerge, resourceAllocation }) => {
      closeResourceMergeDialog();
      if (isMerge) {
        return handleNewMergeResourceAllocation({
          isNewAllocation: resourceMergeDialogAllocation.isNewAllocation,
          overrideScheduleRules,
          smartFitResourceAllocation,
          resourceAllocation
        });
      }

      if (smartFitResourceAllocation.isNewAllocation) {
        return handleAddResourceAllocation({
          projectDetail: project,
          resourceAllocation: smartFitResourceAllocation,
          overrideScheduleRules
        });
      }

      return handleEditResourceAllocation({
        smartFitResourceAllocation,
        overrideScheduleRules
      });
    },
    [
      closeResourceMergeDialog,
      handleAddResourceAllocation,
      handleEditResourceAllocation,
      handleNewMergeResourceAllocation,
      project,
      resourceMergeDialogAllocation,
      smartFitResourceAllocation
    ]
  );

  const createAllocationWithUserAndOverrideRules = useCallback(
    overrideScheduleRules => {
      closeShowSmartFitDialogOrLoading();

      if (isPSARMPMergeResourcesEnabled) {
        setResourceMergeDialogAllocation({
          ...smartFitResourceAllocation,
          scheduleRules: overrideScheduleRules
        });

        return openResourceMergeDialog();
      }

      if (smartFitResourceAllocation.isNewAllocation) {
        return handleAddResourceAllocation({
          projectDetail: project,
          resourceAllocation: smartFitResourceAllocation,
          overrideScheduleRules
        });
      }

      return handleEditResourceAllocation({
        smartFitResourceAllocation,
        overrideScheduleRules
      });
    },
    [
      closeShowSmartFitDialogOrLoading,
      handleAddResourceAllocation,
      handleEditResourceAllocation,
      isPSARMPMergeResourcesEnabled,
      setResourceMergeDialogAllocation,
      openResourceMergeDialog,
      project,
      smartFitResourceAllocation
    ]
  );

  const updatedHandleAllocatedRoleChange = input => {
    const {
      role: { role, resourceAllocation }
    } = input;

    setResourceMergeDialogAllocation({
      ...resourceAllocation,
      user: {
        ...resourceAllocation.user,
        id: resourceAllocation.user.userUri,
        ...resourceAllocation.user.user
      },
      roleUri: role?.id,
      role
    });

    setSmartFitResourceAllocation({
      ...resourceAllocation,
      requestedRoleUri: role ? role.id : null,
      role: role
        ? {
            ...role,
            uri: role.id
          }
        : null
    });

    openResourceMergeDialog();
  };

  const {
    isLoading: isActualsLoading,
    resourceUserActualSeries,
    loadMoreActualRows
  } = useResourceActualSeriesForProjectResources({
    filter,
    sort,
    dateRange: {
      startDate: chartDates[0].start,
      endDate: chartDates[chartDates.length - 1].end
    },
    scale,
    projectUri: project.id,
    isResourceActualModeEnabled
  });

  const loadMoreAllocationsAndActuals = useCallback(() => {
    if (loadMoreActualRows) loadMoreActualRows();
    if (loadMoreRows) loadMoreRows();
  }, [loadMoreRows, loadMoreActualRows]);

  if (!smartFitResourceAllocation.id && (loadingRows || isActualsLoading))
    return <ProjectChartRowsLoading />;

  return (
    <>
      {resourceAllocations.length !== 0 && (
        <GroupTitleHeader
          className={classNames({
            [classes.containerReadonly]: !isEditSwitchEnabled
          })}
        >
          <FormattedMessage id="resourceAllocationUserChartHeaderRow.resources" />
        </GroupTitleHeader>
      )}
      {resourceAllocations.map(resourceAllocation => (
        <ResourceAllocationChartRow
          qeIdTag={`AllocationRow_${resourceAllocation.role?.displayText}`}
          key={resourceAllocation.id}
          resourceAllocation={resourceAllocation}
          project={project}
          isEditSwitchEnabled={isEditSwitchEnabled}
          handleAllocatedUserChange={handleAllocatedUserChange}
          handleAllocatedRoleChange={
            isPSARMPMergeResourcesEnabled
              ? updatedHandleAllocatedRoleChange
              : handleAllocatedRoleChange
          }
          leftComponentWidth={leftComponentWidth}
          handleResourceChangeAndUpdateResourceAllocation={
            handleResourceChangeAndUpdateResourceAllocation
          }
          resourceUserActualSeries={(resourceUserActualSeries || []).find(
            e =>
              e.user.id === resourceAllocation.user.userUri &&
              (e.role?.id === resourceAllocation.role?.id ||
                (resourceAllocation.role === null && e.role === null))
          )}
          createAllocationLoading={createAllocationLoading}
          smartFitResourceAllocation={smartFitResourceAllocation}
          updateAllocationLoading={updateAllocationLoading}
        />
      ))}
      {openSmartFitDialogOrLoadingState && (
        <ShowSmartFitDialogOrLoading
          scale={scale}
          resourceUser={smartFitResourceAllocation.user}
          scheduleRules={
            smartFitResourceAllocation.scheduleRules ||
            defaultSmartFitScheduleRules
          }
          closeShowSmartFitDialogOrLoading={onSmartFitCancel}
          chartStartDate={chartDates.length && chartDates[0].start}
          createAllocationWithUserAndOverrideRules={
            createAllocationWithUserAndOverrideRules
          }
          projectDefaultScheduleRule={project.defaultScheduleRule}
        />
      )}
      {resourceMergeDialogOpen && (
        <ShowResourceMergeDialogOrLoading
          onSubmit={onSubmit}
          resourceMergeDialogOpen={resourceMergeDialogOpen}
          closeResourceMergeDialog={onMergeResourceClose}
          resourceMergeDialogAllocation={resourceMergeDialogAllocation}
        />
      )}

      {!loadingRows && !loadingMore && hasMoreRows && (
        <ShowMoreRow
          loadMore={loadMoreAllocationsAndActuals}
          recordCount={resourceAllocations.length}
          resourceKey="projectResourcing.showMoreRow.resourcesMessage"
        />
      )}
      {loadingMore && <ProjectChartRowsLoading />}
    </>
  );
};

ResourceAllocationChart.propTypes = {
  project: PropTypes.object.isRequired,
  handleAddResourceAllocation: PropTypes.func.isRequired,
  resourceAllocations: PropTypes.array.isRequired,
  scale: PropTypes.string,
  chartDates: PropTypes.array.isRequired,
  isEditSwitchEnabled: PropTypes.bool,
  smartFitResourceAllocation: PropTypes.object,
  openSmartFitDialogOrLoadingState: PropTypes.bool.isRequired,
  closeShowSmartFitDialogOrLoading: PropTypes.func.isRequired,
  loadingRows: PropTypes.bool.isRequired,
  hasMoreRows: PropTypes.bool.isRequired,
  loadingMore: PropTypes.bool.isRequired,
  loadMoreRows: PropTypes.func.isRequired,
  handleNewMergeResourceAllocation: PropTypes.func,
  handleResourceChangeAndUpdateResourceAllocation: PropTypes.func,
  handleEditResourceAllocation: PropTypes.func.isRequired,
  handleAllocatedUserChange: PropTypes.func.isRequired,
  handleAllocatedRoleChange: PropTypes.func.isRequired,
  createAllocationLoading: PropTypes.bool.isRequired,
  updateAllocationLoading: PropTypes.bool.isRequired,
  setSmartFitResourceAllocation: PropTypes.func,
  onSmartFitCancel: PropTypes.func
};

export default ResourceAllocationChart;
